// box z cenami, jednostkami itp - lokalna wersja
// @ts-nocheck

import React, { FC, useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { Printer, Truck, Envelope } from "react-bootstrap-icons";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useSelector } from "store";
import { IProduct } from "api/types";
import {
  useGetProductAddToCartHint,
  useGetCartDeliveryInfo,
  usePostProductQuantityDecrement,
  usePostProductQuantityIncrement,
  usePostProductQuantity,
} from "api";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAppNavigate, useNotifications, useRWD } from "hooks";
import {
  Counter,
  Radio,
  Link,
  Select,
  Availability,
} from "components/controls";
import {
  AddToCartButton,
  AddToShoppingListButton,
  AskProductModal,
} from "components/containers";
import { IUnit } from "api/types";

import { ReactComponent as MessageQuestionIcon } from "assets/messages-question.svg";
import styles from "theme/pages/Product/components/Prices/Prices.module.scss";

// Typ danych wejściowych
interface IProps {
  product: IProduct;
}

// Komponent ikony procentowej z Figmy
const PercentBadgeIcon: FC = () => {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.percentBadgeIcon}
    >
      <path
        d="M14.9992 9.0001L8.99922 15.0001M8.99922 9.0001H9.00922M14.9992 15.0001H15.0092M3.84922 8.6201C3.70326 7.96262 3.72567 7.27894 3.91437 6.63244C4.10308 5.98593 4.45196 5.39754 4.92868 4.92182C5.40541 4.4461 5.99453 4.09844 6.64142 3.91109C7.28832 3.72374 7.97205 3.70276 8.62922 3.8501C8.99093 3.2844 9.48922 2.81886 10.0782 2.49638C10.6671 2.17391 11.3278 2.00488 11.9992 2.00488C12.6707 2.00488 13.3313 2.17391 13.9203 2.49638C14.5092 2.81886 15.0075 3.2844 15.3692 3.8501C16.0274 3.70212 16.7123 3.72301 17.3602 3.91081C18.0081 4.09862 18.598 4.44724 19.0751 4.92425C19.5521 5.40126 19.9007 5.99117 20.0885 6.6391C20.2763 7.28703 20.2972 7.97193 20.1492 8.6301C20.7149 8.99181 21.1805 9.4901 21.5029 10.079C21.8254 10.668 21.9944 11.3286 21.9944 12.0001C21.9944 12.6715 21.8254 13.3322 21.5029 13.9211C21.1805 14.5101 20.7149 15.0084 20.1492 15.3701C20.2966 16.0273 20.2756 16.711 20.0882 17.3579C19.9009 18.0048 19.5532 18.5939 19.0775 19.0706C18.6018 19.5473 18.0134 19.8962 17.3669 20.0849C16.7204 20.2736 16.0367 20.2961 15.3792 20.1501C15.018 20.718 14.5193 21.1855 13.9293 21.5094C13.3394 21.8333 12.6772 22.0032 12.0042 22.0032C11.3312 22.0032 10.669 21.8333 10.0791 21.5094C9.48914 21.1855 8.99045 20.718 8.62922 20.1501C7.97205 20.2974 7.28832 20.2765 6.64142 20.0891C5.99453 19.9018 5.40541 19.5541 4.92868 19.0784C4.45196 18.6027 4.10308 18.0143 3.91437 17.3678C3.72567 16.7213 3.70326 16.0376 3.84922 15.3801C3.27917 15.0193 2.80963 14.5203 2.48426 13.9293C2.1589 13.3384 1.98828 12.6747 1.98828 12.0001C1.98828 11.3255 2.1589 10.6618 2.48426 10.0709C2.80963 9.47992 3.27917 8.98085 3.84922 8.6201Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const Prices: FC<IProps> = ({ product }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { showWarningMessage } = useNotifications();
  const navigate = useAppNavigate();
  const { profile } = useSelector((state) => state.auth);

  // id domyślnego koszyka
  const { currentCartId } = useSelector((state) => state.cart);

  // ilość produktów
  const [quantity, setQuantity] = useState(0);

  // aktualnie zmieniona ilość (sprzed walidacji)
  const [manuallyChangedQuantity, setManuallyChangedQuantity] =
    useState<number>();

  // stan modala zapytania o produkt
  const [isAskProductModalOpen, setIsAskProductModalOpen] = useState(false);

  // aktualne ID jednostki
  const [unitId, setUnitId] = useState<number>(
    product?.product_price_full_info?.prices?.[0]?.unit_id || 0
  );

  // aktualna jednostka
  const unit = useMemo(
    () =>
      product?.product_price_full_info?.prices?.find(
        (unit) => unit.unit_id === unitId
      ),
    [unitId, product]
  );

  // zmiana ilości po zmianie jednostki
  useEffect(() => {
    unit && setQuantity(unit.minimal_quantity || 1);
  }, [unit]);

  // pobranie informacji o zmniejszonej ilości
  const { mutate: decrementQuantity, isLoading: isDecrementQuantityLoading } =
    usePostProductQuantityDecrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      },
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: incrementQuantity, isLoading: isIncrementQuantityLoading } =
    usePostProductQuantityIncrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      },
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: updateQuantity, mutateAsync: updateQuantityAsync } =
    usePostProductQuantity(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      },
    });

  // pobranie informacji z podpowiedzią do koszyka
  const { data: addToCartHintData } = useGetProductAddToCartHint(
    product.id,
    {
      unit_id: unitId,
      quantity,
    },
    { keepPreviousData: true }
  );

  // pobranie podpowiedzi o dostawie
  const { data: cartDeliveryInfoData } = useGetCartDeliveryInfo(
    currentCartId || 0,
    {
      enabled: !!currentCartId,
    }
  );

  const isOpenProfile = profile?.role === "ROLE_OPEN_PROFILE";
  const { isMobile } = useRWD();
  // Select jednostek tylko na mobile (gdy tabela "Typ opakowania" jest ukryta w SCSS przy max-width: 959px)
  const showUnitDropdown = useMediaQuery("(max-width: 959px)");

  // Jednostki produktu (tabela i dropdown oparte o prices – ten sam zestaw co "Typ opakowania")
  const units = product?.product_unit_full_info?.units || [];
  const prices = product?.product_price_full_info?.prices || [];
  const unitOptions = prices.length > 1 ? prices : units;
  const hasMultipleUnits = unitOptions.length > 1;

  // Funkcja renderująca dropdown jednostek (w wierszu z licznikiem i przyciskiem koszyka)
  const renderUnitSelector = () => {
    if (!hasMultipleUnits) {
      return null;
    }

    return (
      <div className={styles.unitSelectorWrapper}>
        <Select<{ unit_id: number; name: string; converter: number }>
          onChange={(item) => item?.unit_id != null && setUnitId(item.unit_id)}
          value={unitId}
          formatOptionLabel={(option) => {
            const u = option.item;
            return (
              <div className={styles.selectLabelWrapper}>
                <div className={styles.selectLabelMain}>{u.name}</div>
                <div className={styles.selectLabelSub}>
                  {u.converter !== 1 ? `(${u.converter})` : ""}
                </div>
              </div>
            );
          }}
          options={unitOptions.map((p) => ({
            value: p.unit_id,
            label: `${"unit_name" in p ? p.unit_name : p.name} ${
              p.converter !== 1 ? `(${p.converter})` : ""
            }`,
            item: {
              unit_id: p.unit_id,
              name: "unit_name" in p ? p.unit_name : p.name,
              converter: p.converter,
            },
          }))}
        />
      </div>
    );
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        "StylePath-Pages-Product-components-Prices"
      )}
    >
      <div className={styles.mainContent}>
        {!isOpenProfile && (
          <div
            className={classnames(styles.price, {
              [styles.priceWithLabel]:
                unit?.price_label === "twoja cena" ||
                unit?.price_label === "najlepsza cena",
            })}>
            <div className={styles.priceRow}>
              <span
                className={classnames(styles.priceLabel, {
                  [styles.priceLabelSpecial]:
                    unit?.price_label === "twoja cena" ||
                    unit?.price_label === "najlepsza cena",
                  [styles.priceLabelDefault]: !unit?.price_label,
                })}
              >
                {unit?.price_label === "twoja cena" && <PercentBadgeIcon />}
                {unit?.price_label ? (
                  <Trans>{unit.price_label}</Trans>
                ) : (
                  <Trans>Cena</Trans>
                )}
                :
              </span>
              <div className={styles.priceValue}>
                {!unit?.price_label && unit?.old_price_net && (
                  <span className={styles.oldPrice}>
                    {unit.old_price_net_formatted} {unit.currency}
                  </span>
                )}
                <strong className={styles.priceAmount}>
                  {unit?.price_net_formatted} {unit?.currency}
                </strong>{" "}
                <span className={styles.nettoLabel}>
                  (<Trans>netto</Trans>)
                </span>
              </div>
            </div>
            <div className={styles.priceGross}>
              {unit?.price_gross_formatted} (<Trans>brutto</Trans>)
            </div>
          </div>
        )}

        {!isOpenProfile && (
          <div className={styles.packagingType}>
            <table className={styles.packagingTable}>
              <thead>
                <tr>
                  <th>
                    <div className={styles.tableHeaderContent}>
                      <span className={styles.tableHeaderTitle}>
                        <Trans>Typ opakowania</Trans>
                      </span>
                      <span className={styles.tableSubtitle}>
                        (szt./opakowanie)
                      </span>
                    </div>
                  </th>
                  <th>
                    <div className={styles.tableHeaderContent}>
                      <span className={styles.tableHeaderTitle}>
                        <Trans>Cena</Trans>
                      </span>
                      <span className={styles.tableSubtitle}>
                        (<Trans>netto</Trans>)
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {prices.map((priceUnit) => (
                  <tr
                    key={priceUnit.id}
                    className={classnames(styles.packagingRow, {
                      [styles.selected]: unitId === priceUnit.unit_id,
                    })}
                    onClick={() => setUnitId(priceUnit.unit_id)}
                  >
                    <td>
                      <div className={styles.packagingRowContent}>
                        <Radio
                          checked={unitId === priceUnit.unit_id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setUnitId(priceUnit.unit_id);
                          }}
                          sx={{
                            color: "#2087DA",
                            "&.Mui-checked": {
                              color: "#2087DA",
                            },
                          }}
                        />
                        <span className={styles.packagingName}>
                          {priceUnit.unit_name} &nbsp;
                          {priceUnit.converter !== 1
                            ? `(${priceUnit.converter})`
                            : ""}
                        </span>
                      </div>
                    </td>
                    <td className={styles.packagingPrice}>
                      {priceUnit.price_net_formatted} {priceUnit.currency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.actionsWrapper}>
          {isOpenProfile ? (
            <Link
              to={`/login?return_url=${encodeURIComponent(
                location.pathname + location.search
              )}`}
              className={styles.loginButton}
            >
              <Trans>zaloguj</Trans>
            </Link>
          ) : (
            <>
              {/* Wybór jednostki tylko na mobile (na PC tylko tabela "Typ opakowania") */}
              {showUnitDropdown && renderUnitSelector()}
              <Counter
                className={styles.quantityCounter}
                onChange={(value) => updateQuantity({ value, unit_id: unitId })}
                onDecrease={() =>
                  decrementQuantity({ value: quantity, unit_id: unitId })
                }
                onIncrease={() =>
                  incrementQuantity({ value: quantity, unit_id: unitId })
                }
                value={quantity}
                onChangeValue={(isChanged, newValue) =>
                  setManuallyChangedQuantity(newValue)
                }
              />
              <AddToCartButton
                isQuantityChanges={
                  !!manuallyChangedQuantity &&
                  manuallyChangedQuantity !== quantity
                }
                updateQuantity={async () => {
                  const newQuantity = manuallyChangedQuantity
                    ? await updateQuantityAsync({
                        value: manuallyChangedQuantity,
                        unit_id: unitId,
                      })
                    : undefined;

                  return newQuantity?.data.value === manuallyChangedQuantity
                    ? manuallyChangedQuantity
                    : undefined;
                }}
                quantity={quantity}
                unitId={unitId}
                productId={product.id}
                large
                disabled={
                  isOpenProfile ||
                  isDecrementQuantityLoading ||
                  isIncrementQuantityLoading
                }
              />
            </>
          )}
        </div>

        {/* Komponent Availability - pod przyciskiem do koszyka, wyrównany do prawej */}
        {!isOpenProfile && product?.product_stock_formatted_full_info && (
          <div className={styles.availabilityWrapper}>
            <Availability
              stock={product.product_stock_formatted_full_info}
              units={product.product_unit_full_info?.units}
              source={product}
            />
          </div>
        )}

        <div className={styles.additionalActions}>
          <div
            className={classnames(styles.actionLink, styles.button)}
            onClick={() => setIsAskProductModalOpen(true)}
          >
            <Envelope />
            <Trans>Zapytaj o produkt</Trans>
          </div>
          {!isOpenProfile && (
            <div className={styles.actionLinkRight}>
              <AddToShoppingListButton
                product_id={product.id}
                unit_id={unit?.unit_id}
                image={
                  product?.product_image_formatted_full_info?.images?.[0]?.thumb
                }
                title={product?.product_basic_full_info?.title}
                index={product?.product_basic_full_info?.index}
                currency={unit?.currency}
                quantity={quantity}
                unit={unit}
              />
            </div>
          )}
        </div>
      </div>

      <AskProductModal
        product={product}
        open={isAskProductModalOpen}
        onClose={() => setIsAskProductModalOpen(false)}
      />
    </div>
  );
};

export default Prices;
